import SecureCookieManager from '../utils/secureCookie';
import { getV2Token } from '../utils/v2AuthUtils';

class AuthService {
  private baseURL: string;
  private csrfToken: string | null = null;
  private csrfTokenPromise: Promise<string> | null = null; // For preventing race conditions
  private requestQueue: Promise<any> = Promise.resolve(); // Queue for sequential API calls
  private sessionCacheData: any | null = null;
  private sessionCacheTimestampMs: number | null = null;
  private sessionInFlightPromise: Promise<any> | null = null;

  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'https://apidemo.pianat.ai'; // Ensure protocol and port
  }

  // Helper method to log CSRF cookies only
  private logCookies(context: string): void {
    // Parse and display only CSRF-related cookies
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const trimmedCookie = cookie.trim();
      const equalIndex = trimmedCookie.indexOf('=');
      
      if (equalIndex > 0) {
        const key = trimmedCookie.substring(0, equalIndex);
        const value = trimmedCookie.substring(equalIndex + 1);
        
        if (key && value && (key.toLowerCase().includes('csrf') || key.toLowerCase().includes('xsrf'))) {
          acc[key] = value;
        }
      }
      return acc;
    }, {} as Record<string, string>);
    
    if (Object.keys(cookies).length > 0) {
      //console.log(`🍪 [${context}] CSRF cookies:`, cookies);
    } else {
      //console.log(`🍪 [${context}] No CSRF cookies found`);
    }
  }

  // Debug CSRF token state
  private debugCsrfState(context: string): void {
    //console.log(`🔍 [${context}] CSRF Debug Info:`);
    //console.log(`  - Cached token: ${this.csrfToken ? this.csrfToken.substring(0, 10) + '...' : 'null'}`);
    //console.log(`  - Raw cookies: ${document.cookie}`);
    
    // Parse CSRF cookies
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      const trimmedCookie = cookie.trim();
      const equalIndex = trimmedCookie.indexOf('=');
      
      if (equalIndex > 0) {
        const key = trimmedCookie.substring(0, equalIndex);
        const value = trimmedCookie.substring(equalIndex + 1);
        
        if (key && value && (key.toLowerCase().includes('csrf') || key.toLowerCase().includes('xsrf'))) {
          acc[key] = value;
        }
      }
      return acc;
    }, {} as Record<string, string>);
    
    //console.log(`  - CSRF cookies:`, cookies);
  }

  // Get CSRF token - always fresh for single-use tokens with race condition protection
  private async getCsrfToken(): Promise<string> {
    // If there's already a token fetch in progress, wait for it
    if (this.csrfTokenPromise) {
      //console.log('Waiting for existing CSRF token fetch...');
      return await this.csrfTokenPromise;
    }
    
    // Always get a fresh token since backend marks them as "used" after first request
    this.clearCsrfToken();
    
    // Add a small delay to prevent overwhelming the server
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // Create a new promise for this token fetch
    this.csrfTokenPromise = this.fetchCsrfToken();
    
    try {
      const token = await this.csrfTokenPromise;
      return token;
    } finally {
      // Clear the promise when done
      this.csrfTokenPromise = null;
    }
  }

  // Actual CSRF token fetching logic
  private async fetchCsrfToken(): Promise<string> {
    try {
      //console.log('Fetching fresh CSRF token...');
      const response = await fetch(`${this.baseURL}/csrf/token`, { 
        method: "GET", 
        credentials: "include" 
      });
      
      if (!response.ok) {
        console.error('CSRF token fetch failed with status:', response.status);
        throw new Error(`CSRF token fetch failed: ${response.status}`);
      }
      
      const data = await response.json();

      if (!data.csrfToken) {
        console.error('No CSRF token in response:', data);
        throw new Error('No CSRF token in response');
      }

      this.csrfToken = data.csrfToken;
      //console.log('Fresh CSRF token cached:', this.csrfToken?.substring(0, 10) + '...');
      
      // Debug the token state
      this.debugCsrfState('After fetching CSRF token');
      return this.csrfToken || '';
    } catch (error) {
      console.error('Failed to get CSRF token:', error);
      return '';
    }
  }

  // Clear cached CSRF token
  private clearCsrfToken(): void {
    //console.log('Clearing CSRF token cache');
    this.csrfToken = null;
    this.csrfTokenPromise = null; // Also clear any pending promise
    
    // Also clear CSRF token from cookies
    document.cookie = 'ntra_compliance_csrf_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    //console.log('CSRF token cleared from cookies');
  }

  // Clear request queue (useful for logout or page refresh)
  private clearRequestQueue(): void {
    //console.log('Clearing request queue');
    this.requestQueue = Promise.resolve();
  }

  // Get stored token from secure cookie or v2-frontend cookies
  private getToken(): string | null {
    // First try to get token from front_compliance's own cookie
    const ownToken = SecureCookieManager.getAuthToken();
    if (ownToken) {
      return ownToken;
    }
    
    // If no own token, try to get from v2-frontend cookie format
    // This allows sharing the same backend authentication
    const v2Token = getV2Token();
    if (v2Token) {
      return v2Token;
    }
    
    return null;
  }

  // Login method - same as v2-frontend and landing
  async login(username: string, password: string, rememberMe: boolean = false) {
    try {
      // Clear any cached tokens before login
      this.clearCsrfToken();
      
      let csrfToken = await this.getCsrfToken();
      
      let response = await fetch(`${this.baseURL}/auth/loginDemo`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        credentials: "include",
        body: JSON.stringify({ username, password, remmberMe: rememberMe })
      });

      // If token was already used, clear cache and get new token
      if (response.status === 403) {
        this.logCookies('On 403 error - current CSRF cookies');
        this.clearCsrfToken();
        csrfToken = await this.getCsrfToken();
        
        response = await fetch(`${this.baseURL}/auth/loginDemo`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          },
          credentials: "include",
          body: JSON.stringify({ username, password, remmberMe: rememberMe })
        });
      }
      
      // Same error handling as v2-frontend
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Login failed');
      }

      const data = await response.json();
      
      // Same return format as v2-frontend (return data directly, not wrapped)
      return data;
    } catch (error: any) {
      // Same error throwing as v2-frontend
      throw new Error(error.message || 'Login failed');
    }
  }

  // Logout method
  async logout() {
    try {
      (window as any).__LOGGING_OUT__ = true; // Set global flag
      this.clearCsrfToken();
      const csrfToken = await this.getCsrfToken();
      await fetch(`${this.baseURL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        credentials: "include"
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      SecureCookieManager.clearAuthCookies();
      try {
        document.cookie = `ntra_compliance_csrf_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        document.cookie = `i18next=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      } catch {}
      this.clearCsrfToken();
      this.clearRequestQueue();
      this.sessionCacheData = null;
      this.sessionCacheTimestampMs = null;
      this.sessionInFlightPromise = null;
      (window as any).__LOGGING_OUT__ = false; // Reset global flag after cleanup
    }
  }

  // Check authentication
  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  // Get session from HttpOnly cookies
  async getSession(forceFresh: boolean = false) {
    const now = Date.now();
    const CACHE_TTL_MS = 30_000; // 30 seconds

    if (!forceFresh && this.sessionCacheData && this.sessionCacheTimestampMs && (now - this.sessionCacheTimestampMs) < CACHE_TTL_MS) {
      return this.sessionCacheData;
    }

    if (this.sessionInFlightPromise) {
      return this.sessionInFlightPromise;
    }

    this.sessionInFlightPromise = (async () => {
      try {
        const csrfToken = await this.getCsrfToken();
        const response = await fetch(`${this.baseURL}/auth/session`, {
          method: 'POST', // Use POST for CSRF compatibility
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          }
        });
        const data = await response.json();
        if (response.ok && data?.isSuccess) {
          this.sessionCacheData = data;
          this.sessionCacheTimestampMs = Date.now();
        } else {
          this.sessionCacheData = null;
          this.sessionCacheTimestampMs = null;
        }
        return data;
      } catch (e) {
        this.sessionCacheData = null;
        this.sessionCacheTimestampMs = null;
        return { isSuccess: false };
      } finally {
        this.sessionInFlightPromise = null;
      }
    })();

    return this.sessionInFlightPromise;
  }

  // Get current user information.
  // Prefer using token + backend (via userServicer.getUserInfo) in new code.
  // This method is kept for backward compatibility and simply returns null
  // to avoid relying on the removed `user` cookie.
  getCurrentUser() {
    return null;
  }

  // Validate token
  async validateToken(token: string) {
    // Add validation to the request queue to prevent conflicts
    this.requestQueue = this.requestQueue.then(async () => {
      return await this.executeValidateToken(token);
    }).catch(async (error) => {
      console.error('Validate token queue error:', error);
      return await this.executeValidateToken(token);
    });

    return this.requestQueue;
  }

  // Actual token validation execution
  private async executeValidateToken(token: string) {
    try {
      // Clear any cached tokens before validation
      this.clearCsrfToken();
      let csrfToken = await this.getCsrfToken();
      
      //console.log(`Validating token with CSRF: ${csrfToken.substring(0, 10)}...`);
      this.debugCsrfState('Before validate token request');
      
      let response = await fetch(`${this.baseURL}/auth/validate-token`, {
        method: 'POST',
        credentials: "include",
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({ token }),
      });

      // If CSRF token was already used, retry with fresh token
      if (response.status === 403) {
        //console.log('CSRF token used during validation, retrying with fresh token...');
        this.logCookies('On validate token 403 error - current CSRF cookies');
        this.clearCsrfToken();
        csrfToken = await this.getCsrfToken();
        
        //console.log(`Retrying validation with fresh CSRF: ${csrfToken.substring(0, 10)}...`);
        response = await fetch(`${this.baseURL}/auth/validate-token`, {
          method: 'POST',
          credentials: "include",
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
          },
          body: JSON.stringify({ token }),
        });
      }

      if (!response.ok) {
        this.logCookies('On validate token error - current CSRF cookies');
        console.error('Validate token failed with status:', response.status);
        throw new Error(`Failed to validate token: ${response.status}`);
      }

      const data = await response.json();
      //console.log('Token validation successful');
      return data;
    } catch (error: any) {
      console.error('Token validation error:', error);
      this.debugCsrfState('After validate token error');
      throw new Error(error.message || 'Token validation failed');
    }
  }

  // Refresh token
  async refreshToken() {
    // Add refresh to the request queue to prevent conflicts
    this.requestQueue = this.requestQueue.then(async () => {
      return await this.executeRefreshToken();
    }).catch(async (error) => {
      console.error('Refresh token queue error:', error);
      return await this.executeRefreshToken();
    });

    return this.requestQueue;
  }

  // Actual refresh token execution
  private async executeRefreshToken() {
    try {
      const refreshToken = SecureCookieManager.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Clear any cached tokens before refresh
      this.clearCsrfToken();
      let csrfToken = await this.getCsrfToken();
      
      //console.log(`Refreshing token with CSRF: ${csrfToken.substring(0, 10)}...`);
      this.debugCsrfState('Before refresh token request');
      
      let response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        credentials: "include",
        body: JSON.stringify({ refreshToken })
      });

      // If CSRF token was already used, retry with fresh token
      if (response.status === 403) {
        //console.log('CSRF token used during refresh, retrying with fresh token...');
        this.logCookies('On refresh token 403 error - current CSRF cookies');
        this.clearCsrfToken();
        csrfToken = await this.getCsrfToken();
        
        //console.log(`Retrying refresh with fresh CSRF: ${csrfToken.substring(0, 10)}...`);
        response = await fetch(`${this.baseURL}/auth/refresh`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          },
          credentials: "include",
          body: JSON.stringify({ refreshToken })
        });
      }

      const data = await response.json();

      if (data.isSuccess) {
        // Tokens are now set as HttpOnly cookies by the backend
        // No need to store them client-side
        //console.log('Token refresh successful');
        return data.data.accessToken;
      } else {
        this.logCookies('On refresh token error - current CSRF cookies');
        console.error('Refresh token failed:', data.message);
        throw new Error(data.message);
      }
    } catch (error: any) {
      console.error('Refresh token error:', error);
      this.logCookies('On refresh token exception - current CSRF cookies');
      this.debugCsrfState('After refresh token error');
      this.logout();
      throw error;
    }
  }

  // API call with automatic token refresh and sequential processing
  async apiCall(url: string, options: RequestInit = {}) {
    // Add this request to the queue and wait for previous requests to complete
    this.requestQueue = this.requestQueue.then(async () => {
      return await this.executeApiCall(url, options);
    }).catch(async (error) => {
      console.error('Request queue error:', error);
      return await this.executeApiCall(url, options);
    });

    return this.requestQueue;
  }

  // Actual API call execution
  private async executeApiCall(url: string, options: RequestInit = {}) {
    const token = this.getToken();
    const csrfToken = await this.getCsrfToken();
    
    // Check if body is FormData to handle file uploads
    const isFormData = options.body instanceof FormData;
    
    const config: RequestInit = {
      ...options,
      credentials: "include",
      headers: {
        // Don't set Content-Type for FormData - browser will set it automatically with boundary
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        'Authorization': token ? `Bearer ${token}` : '',
        'X-CSRF-TOKEN': csrfToken,
        ...options.headers
      }
    };

    try {
      //console.log(`Making request to ${url} with CSRF token: ${csrfToken.substring(0, 10)}...`);
      this.debugCsrfState('Before making request');
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        // Try to refresh token
        try {
          const newToken = await this.refreshToken();
          config.headers = {
            ...config.headers,
            'Authorization': `Bearer ${newToken}`
          };
          const retryResponse = await fetch(url, config);
          return retryResponse;
        } catch (refreshError) {
          this.logout();
          // Redirect to login at root path
          window.location.href = '/login';
          throw new Error('Session expired');
        }
      }
      
      // If still getting 403, something is wrong with CSRF setup
      if (response.status === 403) {
        this.logCookies(`On 403 error for ${url} - current CSRF cookies`);
        console.error('CSRF token still failing after fresh fetch - check backend setup');
        
        // Try one more time with a completely fresh token and longer delay
        //console.log('Attempting final retry with fresh CSRF token...');
        this.clearCsrfToken();
        await new Promise(resolve => setTimeout(resolve, 100)); // Longer delay
        const freshCsrfToken = await this.getCsrfToken();
        config.headers = {
          ...config.headers,
          'X-CSRF-TOKEN': freshCsrfToken
        };
        
        const finalResponse = await fetch(url, config);
        if (finalResponse.status === 403) {
          console.error('Final retry also failed - CSRF setup issue');
          // Log detailed debug info
          this.debugCsrfState('After final 403 failure');
        }
        return finalResponse;
      }
      
      return response;
    } catch (error) {
      this.logCookies(`On API call error for ${url} - current CSRF cookies`);
      console.error('Request failed:', error);
      throw error;
    }
  }

  // Register user
  async register(userData: any) {
    // Add registration to the request queue to prevent conflicts
    this.requestQueue = this.requestQueue.then(async () => {
      return await this.executeRegister(userData);
    }).catch(async (error) => {
      console.error('Register queue error:', error);
      return await this.executeRegister(userData);
    });

    return this.requestQueue;
  }

  // Actual registration execution
  private async executeRegister(userData: any) {
    try {
      // Clear any cached tokens before registration
      this.clearCsrfToken();
      let csrfToken = await this.getCsrfToken();
      
      //console.log(`Registering user with CSRF: ${csrfToken.substring(0, 10)}...`);
      this.debugCsrfState('Before register request');
      
      let response = await fetch(`${this.baseURL}/auth/registerReg`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        credentials: "include",
        body: JSON.stringify(userData)
      });

      // If CSRF token was already used, retry with fresh token
      if (response.status === 403) {
        //console.log('CSRF token used during registration, retrying with fresh token...');
        this.logCookies('On register 403 error - current CSRF cookies');
        this.clearCsrfToken();
        csrfToken = await this.getCsrfToken();
        
        //console.log(`Retrying registration with fresh CSRF: ${csrfToken.substring(0, 10)}...`);
        response = await fetch(`${this.baseURL}/auth/registerReg`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken
          },
          credentials: "include",
          body: JSON.stringify(userData)
        });
      }

      const data = await response.json();

      if (data.isSuccess) {
        //console.log('Registration successful');
        return { success: true, message: data.message };
      } else {
        this.logCookies('On register error - current CSRF cookies');
        console.error('Registration failed:', data.message);
        return { success: false, message: data.message };
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      this.logCookies('On register exception - current CSRF cookies');
      this.debugCsrfState('After register error');
      return { success: false, message: 'Registration failed. Please try again.' };
    }
  }
}

export default new AuthService(); 